const berlinTimeZone = "Europe/Berlin"
const dayMs = 24 * 60 * 60 * 1000
const weekMs = 7 * dayMs

type DateParts = {
  year: number
  month: number
  day: number
}

export type LetterDeliveryWindowPresentation = {
  weekLabel: string | null
  announcementSubLabel: string
  actionPendingSubLabel: string
  tooltipValue: string | null
  searchText: string
}

const berlinDateFormatter = new Intl.DateTimeFormat("de-DE", {
  timeZone: berlinTimeZone,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

const datePartsInBerlin = (value: Date | string | null | undefined): DateParts | null => {
  if (!value) return null

  const date = value instanceof Date ? value : new Date(value)

  if (Number.isNaN(date.getTime())) return null

  const parts = berlinDateFormatter.formatToParts(date)
  const year = Number(parts.find((part) => part.type === "year")?.value)
  const month = Number(parts.find((part) => part.type === "month")?.value)
  const day = Number(parts.find((part) => part.type === "day")?.value)

  if (!year || !month || !day) return null

  return { year, month, day }
}

const toUtcDate = ({ year, month, day }: DateParts) => new Date(Date.UTC(year, month - 1, day))

const startOfIsoWeek = (date: Date) => {
  const clone = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
  const dayOfWeek = clone.getUTCDay() || 7
  clone.setUTCDate(clone.getUTCDate() - dayOfWeek + 1)

  return clone
}

const addDays = (date: Date, days: number) => {
  const clone = new Date(date)
  clone.setUTCDate(clone.getUTCDate() + days)

  return clone
}

const dayLabel = (date: Date) =>
  `${String(date.getUTCDate()).padStart(2, "0")}.${String(date.getUTCMonth() + 1).padStart(2, "0")}.`

const relativeWeekLabel = (weekStart: Date, now: Date) => {
  const currentWeekStart = startOfIsoWeek(
    toUtcDate(
      datePartsInBerlin(now) ?? {
        year: now.getUTCFullYear(),
        month: now.getUTCMonth() + 1,
        day: now.getUTCDate(),
      },
    ),
  )
  const weekDistance = Math.round((weekStart.getTime() - currentWeekStart.getTime()) / weekMs)

  if (weekDistance === 1) return "nächste Woche"

  if (weekDistance === 2) return "übernächste Woche"

  return null
}

export const buildLetterDeliveryWindowPresentation = (
  value: Date | string | null | undefined,
  { now = new Date() }: { now?: Date } = {},
): LetterDeliveryWindowPresentation => {
  const deliveryDate = datePartsInBerlin(value)

  if (!deliveryDate) {
    return {
      weekLabel: null,
      announcementSubLabel: "Wochen-To-do",
      actionPendingSubLabel: "Wochen-To-do",
      tooltipValue: null,
      searchText: "Wochen-To-do",
    }
  }

  const weekStart = startOfIsoWeek(toUtcDate(deliveryDate))
  const weekEnd = addDays(weekStart, 4)
  const weekLabel =
    relativeWeekLabel(weekStart, now) ?? `Woche vom ${dayLabel(weekStart)} bis ${dayLabel(weekEnd)}`

  return {
    weekLabel,
    announcementSubLabel: `zuzustellen: ${weekLabel}`,
    actionPendingSubLabel: `frühestens ${weekLabel}`,
    tooltipValue: weekLabel,
    searchText: weekLabel,
  }
}
